import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Lightning, CurrencyCircleDollar, Info, CheckCircle, ShieldWarning, ArrowsClockwise, ClockCountdown, XCircle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import NovationAgreement from '@/components/NovationAgreement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { INR: '\u20B9', USD: '$', EUR: '\u20AC', GBP: '\u00A3' };

export default function CycleCalculator({ currency, onUnitsChange, refreshKey }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;

  const [calc, setCalc] = useState(null);
  const [node, setNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [engineStatus, setEngineStatus] = useState(null);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementMethod, setAgreementMethod] = useState('now');
  const [submitting, setSubmitting] = useState(false);
  const [bankVerified, setBankVerified] = useState(false);
  const [recompute, setRecompute] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [updating, setUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [calcRes, balRes, engRes, kycRes, recRes, certRes] = await Promise.all([
        axios.get(`${API}/cycle/calculate`, { withCredentials: true }),
        axios.get(`${API}/cycle/balance`, { withCredentials: true }).catch(() => ({ data: { balance: 0 } })),
        axios.get(`${API}/engine/status`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/kyc/status`, { withCredentials: true }).catch(() => ({ data: { bank_verified: false } })),
        axios.get(`${API}/node/recompute`, { withCredentials: true }).catch(() => ({ data: { active: false } })),
        axios.get(`${API}/certificates`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCalc(calcRes.data);
      setNode(calcRes.data.node);
      setBankVerified(kycRes.data.bank_verified);
      setRecompute(recRes.data);
      setCertificates(certRes.data || []);
      if (onUnitsChange) onUnitsChange(balRes.data.balance || 0);
      if (engRes.data) setEngineStatus(engRes.data);
    } catch (err) {
      console.error('calc fetch failed', err);
    } finally { setLoading(false); }
  }, [onUnitsChange]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (refreshKey > 0) fetchData(); }, [refreshKey]); // eslint-disable-line

  useEffect(() => {
    if (!document.getElementById('razorpay-script')) {
      const s = document.createElement('script');
      s.id = 'razorpay-script';
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  const openAgreement = (method) => { setAgreementMethod(method); setAgreementOpen(true); };

  const handleAuthorize = async (method) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/node/initiate`,
        { method, agreement: { signed: true } }, { withCredentials: true });
      const data = res.data;
      // Pay Now in production -> open Razorpay checkout
      if (method === 'now' && data.order_id) {
        const options = {
          key: data.key_id, amount: data.amount, currency: 'INR',
          name: 'Diametor', description: 'Platform Fee',
          order_id: data.order_id,
          handler: async (response) => {
            await axios.post(`${API}/node/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { withCredentials: true });
            toast.success('Platform fee paid! The engine will now route your obligations.');
            setAgreementOpen(false);
            await fetchData();
          },
          prefill: { name: data.user_name, email: data.user_email, contact: data.user_phone },
          theme: { color: '#FACC15' },
          modal: { ondismiss: () => setSubmitting(false) },
        };
        new window.Razorpay(options).open();
        return;
      }
      // Pay Later in production -> open Razorpay mandate (subscription) checkout popup
      if (method === 'later' && data.subscription_id && !data.test_mode) {
        const options = {
          key: data.key_id,
          subscription_id: data.subscription_id,
          name: 'Diametor',
          description: 'Platform Fee Mandate (charged on cycle execution)',
          handler: async (response) => {
            await axios.post(`${API}/node/confirm-mandate`, {
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { withCredentials: true });
            toast.success('Mandate authorized! The engine will charge the fee when it clears your obligations.');
            setAgreementOpen(false);
            await fetchData();
          },
          prefill: { name: data.user_name, email: data.user_email, contact: data.user_phone },
          theme: { color: '#FACC15' },
          modal: { ondismiss: () => setSubmitting(false) },
        };
        new window.Razorpay(options).open();
        return;
      }
      if (method === 'later' && data.short_url && data.short_url.startsWith('http')) {
        window.open(data.short_url, '_blank');
        toast.info('Authorize the mandate in the new tab, then return here.');
      } else if (data.test_mode) {
        toast.success(method === 'now'
          ? 'Platform fee paid (test mode). Engine will route your obligations.'
          : 'Mandate active (test mode). Engine will route your obligations.');
      }
      setAgreementOpen(false);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authorization failed');
    } finally { setSubmitting(false); }
  };

  const handleCancelMandate = async () => {
    try {
      await axios.post(`${API}/node/cancel-mandate`, {}, { withCredentials: true });
      toast.info('Mandate cancelled.');
      await fetchData();
    } catch { toast.error('Failed to cancel mandate'); }
  };

  const refreshMandate = async () => {
    try {
      const res = await axios.get(`${API}/node/mandate-status`, { withCredentials: true });
      toast.info(`Mandate: ${res.data.mandate_status}`);
      await fetchData();
    } catch { toast.error('Could not refresh mandate'); }
  };

  const handleUpdateResume = async () => {
    setUpdating(true);
    try {
      const res = await axios.post(`${API}/node/update`, {}, { withCredentials: true });
      const data = res.data;
      if (data.needs_payment && data.order_id) {
        const options = {
          key: data.key_id, amount: data.amount, currency: 'INR',
          name: 'Diametor', description: 'Platform Fee Top-up', order_id: data.order_id,
          handler: async (response) => {
            await axios.post(`${API}/node/update-verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { withCredentials: true });
            toast.success('Top-up paid. Clearance cycle resumed.');
            await fetchData();
          },
          prefill: { name: data.user_name, email: data.user_email, contact: data.user_phone },
          theme: { color: '#FACC15' }, modal: { ondismiss: () => setUpdating(false) },
        };
        new window.Razorpay(options).open();
        return;
      }
      toast.success(data.message || 'Clearance updated and resumed');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setUpdating(false); }
  };

  const handleStartNew = async () => {
    try {
      await axios.post(`${API}/node/reset`, {}, { withCredentials: true });
      toast.success('Ready for a new clearance cycle');
      await fetchData();
    } catch { toast.error('Failed to reset'); }
  };

  if (loading || !calc) {
    return <div className={`p-6 border rounded-md mt-6 ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}><div className="animate-pulse h-40" /></div>;
  }

  const feeActive = node && ['paid', 'mandate_active', 'charged'].includes(node.fee_status);
  const mandatePending = node && ['mandate_pending'].includes(node.fee_status);
  const needsUpdate = recompute?.active && (recompute.needs_topup || recompute.surplus > 0);

  // ---- CLEARANCE COMPLETE: all obligations settled ----
  if (feeActive && recompute?.no_active_obligations) {
    return (
      <div className="mt-6 space-y-4" data-testid="clearance-complete-area">
        <div className={`p-5 border rounded-md ${isDark ? 'border-green-800 bg-green-500/5' : 'border-green-200 bg-green-50'}`}>
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={22} weight="bold" className="text-green-500" />
            <h3 className={`font-outfit text-lg font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Clearance Complete</h3>
          </div>
          <p className={`font-ibm text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>All your dues and receivables have been cleared. Below is your settlement report.</p>
        </div>

        <div>
          <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Cleared Accounts</p>
          <div className="space-y-2">
            {certificates.length === 0 ? (
              <p className={`font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No cleared records.</p>
            ) : certificates.map((c, idx) => (
              <div key={c.certificate_id} className={`p-3 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`cleared-account-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`font-outfit text-sm font-bold ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>{idx + 1}</span>
                    <span className={`font-ibm text-[10px] px-1.5 py-0.5 rounded-sm ${c.cert_type === 'payment' ? 'bg-blue-500/10 text-blue-400' : 'bg-green-500/10 text-green-500'}`}>{c.cert_type === 'payment' ? 'Payable Paid' : 'Receivable Discharged'}</span>
                    <span className={`font-ibm text-sm ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(c.total_cleared || 0).toLocaleString()}</span>
                  </div>
                  <button onClick={() => navigate('/transactions')} className="font-ibm text-[10px] font-semibold uppercase tracking-wider text-yellow-400 hover:text-yellow-500" data-testid={`view-cleared-cert-${idx}`}>Certificate</button>
                </div>
                <p className={`font-ibm text-[10px] mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{(c.cleared_debts || []).map(d => d.counterparty).join(', ')}</p>
              </div>
            ))}
          </div>
        </div>

        <button onClick={handleStartNew} className="w-full h-12 bg-yellow-400 text-zinc-950 font-outfit text-base font-bold rounded-sm hover:bg-yellow-500 active:scale-[0.98] transition-all flex items-center justify-center gap-2" data-testid="start-new-cycle-btn">
          <Lightning size={18} weight="bold" /> Start a New Clearance Cycle
        </button>
      </div>
    );
  }

  // ---- ACTIVE STATE: engine routing ----
  if (feeActive) {
    return (
      <div className="mt-6 space-y-4" data-testid="node-active-area">
        {needsUpdate && (
          <div className={`p-4 border rounded-md ${isDark ? 'border-yellow-400/40 bg-yellow-400/5' : 'border-yellow-300 bg-yellow-50'}`} data-testid="fee-update-banner">
            <div className="flex items-start gap-2 mb-3">
              <Info size={18} weight="bold" className="text-yellow-400 shrink-0 mt-0.5" />
              <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                {recompute.needs_topup ? 'Your obligations changed — recalculated platform fee' : 'Your obligations reduced — surplus refund due'}
              </p>
            </div>

            {/* Exact TOR-style recalculation */}
            <div className={`p-3 rounded-sm border ${isDark ? 'border-zinc-800 bg-zinc-900/60' : 'border-zinc-200 bg-white'}`} data-testid="fee-recalc-breakdown">
              <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Recalculated Platform Fee</p>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>10% of what you owe ({symbol}{(recompute.total_creditors || 0).toLocaleString()})</span>
                  <span className={`font-ibm text-xs font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(recompute.creditor_fee || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>5% of what's owed to you ({symbol}{(recompute.total_debtors || 0).toLocaleString()})</span>
                  <span className={`font-ibm text-xs font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(recompute.debtor_fee || 0).toLocaleString()}</span>
                </div>
                <div className={`border-t pt-1.5 flex justify-between items-center ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                  <span className={`font-ibm text-xs font-semibold ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>New total platform fee</span>
                  <span className={`font-outfit text-sm font-bold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(recompute.current_fee || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Already paid</span>
                  <span className={`font-ibm text-xs font-semibold text-green-500`} data-testid="fee-already-paid">- {symbol}{(recompute.paid_fee || 0).toLocaleString()}</span>
                </div>
                <div className={`border-t pt-1.5 flex justify-between items-center ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                  {recompute.needs_topup ? (
                    <>
                      <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>Balance to pay</span>
                      <span className="font-outfit text-base font-bold text-yellow-400" data-testid="fee-balance-to-pay">{symbol}{(recompute.delta || 0).toLocaleString()}</span>
                    </>
                  ) : (
                    <>
                      <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-green-500' : 'text-green-600'}`}>Surplus to refund</span>
                      <span className="font-outfit text-base font-bold text-green-500" data-testid="fee-surplus-amount">{symbol}{(recompute.surplus || 0).toLocaleString()}</span>
                    </>
                  )}
                </div>
                {recompute.needs_topup && node.fee_method === 'now' && (recompute.topup_amount || 0) !== (recompute.delta || 0) && (
                  <div className="flex justify-between items-center">
                    <span className={`font-ibm text-[11px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Pay Now price (12% off the balance)</span>
                    <span className={`font-ibm text-xs font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>{symbol}{(recompute.topup_amount || 0).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            {recompute.needs_topup ? (
              <p className={`font-ibm text-[11px] mt-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                {node.fee_method === 'now'
                  ? `Pay the balance of ${symbol}${(recompute.topup_amount || 0).toLocaleString()} to update and resume your clearance cycle.`
                  : (recompute.can_pay_later
                    ? `Your mandate cap will be raised to ${symbol}${(recompute.current_fee || 0).toLocaleString()}; the extra ${symbol}${(recompute.delta || 0).toLocaleString()} is charged at execution.`
                    : `Your fee now exceeds ${symbol}${(recompute.pay_later_max_fee || 15000).toLocaleString()}, so the mandate can't cover it — please Pay Now instead.`)}
              </p>
            ) : (
              <p className={`font-ibm text-[11px] mt-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                A surplus of {symbol}{(recompute.surplus || 0).toLocaleString()} will be refunded to your paid account within 90 days after this cycle completes.
              </p>
            )}

            <button onClick={handleUpdateResume} disabled={updating} className="mt-3 h-9 px-4 bg-yellow-400 text-zinc-950 font-ibm text-xs font-semibold rounded-sm hover:bg-yellow-500 disabled:opacity-50 flex items-center gap-1" data-testid="update-resume-btn">
              {updating ? <div className="animate-spin w-4 h-4 border-2 border-zinc-950 border-t-transparent rounded-full" /> : <ArrowsClockwise size={13} weight="bold" />}
              {recompute.needs_topup
                ? (node.fee_method === 'now'
                  ? `Pay ${symbol}${(recompute.topup_amount || 0).toLocaleString()} Now & Resume`
                  : (recompute.can_pay_later ? 'Update Mandate & Resume' : `Pay ${symbol}${(recompute.topup_amount || recompute.delta || 0).toLocaleString()} Now`))
                : 'Update and Resume Clearance Cycle'}
            </button>
          </div>
        )}
        <div className={`border rounded-md overflow-hidden ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
          <div className={`p-5 ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
            <div className="flex items-center gap-2 mb-3">
              <ArrowsClockwise size={20} weight="bold" className="text-yellow-400 animate-spin" style={{ animationDuration: '3s' }} />
              <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Clearance Cycle Engine Active</h3>
            </div>
            <div className={`p-3 rounded-sm border ${isDark ? 'border-yellow-400/20 bg-yellow-400/5' : 'border-yellow-200 bg-yellow-50'}`}>
              <p className={`font-ibm text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                The engine is mapping interlocking cycles to legally wipe out your liabilities and bring in your receivables via multilateral novation.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3">
                <div><span className={`font-ibm text-[10px] uppercase ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Platform Fee</span><p className={`font-outfit text-base font-bold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(node.amount_payable || 0).toLocaleString()}</p></div>
                <div><span className={`font-ibm text-[10px] uppercase ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Method</span><p className={`font-outfit text-base font-bold capitalize ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{node.fee_method === 'now' ? 'Paid Upfront' : 'Mandate'}</p></div>
                <div><span className={`font-ibm text-[10px] uppercase ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Status</span><p className="font-outfit text-base font-bold text-green-500 capitalize">{node.fee_status === 'mandate_active' ? 'Mandate Active' : (node.fee_status === 'charged' ? 'Fee Charged' : 'Paid')}</p></div>
              </div>
            </div>
          </div>
        </div>

        {/* KYC prompt */}
        {!bankVerified && (
          <div className={`p-4 border rounded-md ${isDark ? 'border-yellow-400/30 bg-yellow-400/5' : 'border-yellow-200 bg-yellow-50'}`} data-testid="kyc-prompt-card">
            <div className="flex items-start gap-2">
              <ShieldWarning size={18} weight="bold" className="text-yellow-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>Complete KYC to receive payouts</p>
                <p className={`font-ibm text-xs mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Add your bank details so the engine can include you in cycles and pay you when you are owed money.</p>
                <button onClick={() => navigate('/kyc')} className="mt-2 h-8 px-4 bg-yellow-400 text-zinc-950 font-ibm text-xs font-semibold rounded-sm hover:bg-yellow-500" data-testid="goto-kyc-btn">Complete KYC</button>
              </div>
            </div>
          </div>
        )}

        {/* Mandate controls */}
        {node.fee_method === 'later' && (
          <div className={`p-3 border rounded-sm flex items-center justify-between ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`}>
            <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Mandate will be charged when a cycle clears your obligations.</span>
            <button onClick={handleCancelMandate} className="font-ibm text-[10px] font-semibold uppercase tracking-wider text-red-400 hover:text-red-300 flex items-center gap-1" data-testid="cancel-mandate-btn"><XCircle size={12} weight="bold" /> Cancel</button>
          </div>
        )}

        {/* Engine status */}
        {engineStatus?.last_run && (
          <div className={`p-3 border rounded-sm ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="engine-status-indicator">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Cycle Engine Active (hourly)</span>
              </div>
              <span className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{engineStatus.total_settlements} cycles cleared</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- MANDATE PENDING ----
  if (mandatePending) {
    return (
      <div className="mt-6 space-y-4" data-testid="mandate-pending-area">
        <div className={`p-5 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <ClockCountdown size={20} weight="bold" className="text-yellow-400" />
            <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Authorize Your Mandate</h3>
          </div>
          <p className={`font-ibm text-xs mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            This mandate will be executed when the cycle engine maps an interlocking cycle that can legally wipe out your liability or bring in your receivable. Keep your account funded for the {symbol}{(node.amount_payable || 0).toLocaleString()} fee. You may pause or cancel anytime, but the engine will then ignore your obligations.
          </p>
          <div className={`p-3 mb-4 rounded-sm border ${isDark ? 'border-zinc-700 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`}>
            <p className={`font-ibm text-[11px] ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`} data-testid="mandate-charge-note">
              Note: We will charge <strong>{symbol}1</strong> while the mandate is being set up (this appears in the Razorpay window) — your actual platform fee is only collected when a cycle clears your obligations.
            </p>
            <p className={`font-ibm text-[11px] mt-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`} data-testid="mandate-cancel-hint">
              Please press <strong>Cancel</strong> if you wish to go back to Tor calculations or choose <strong>Pay Now</strong> instead.
            </p>
          </div>
          <div className="flex gap-2">
            {node.short_url && node.short_url.startsWith('http') && (
              <a href={node.short_url} target="_blank" rel="noreferrer" className="flex-1 h-10 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 flex items-center justify-center" data-testid="authorize-mandate-link">Authorize Mandate</a>
            )}
            <button onClick={refreshMandate} className={`h-10 px-4 font-ibm text-sm rounded-sm border ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-zinc-200 text-zinc-700'}`} data-testid="refresh-mandate-btn">Refresh Status</button>
            <button onClick={handleCancelMandate} className={`h-10 px-4 font-ibm text-sm rounded-sm border ${isDark ? 'border-red-900 text-red-400' : 'border-red-200 text-red-500'}`}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- DEFAULT: Fee calculator + Pay options ----
  const { total_creditors, total_debtors, creditor_fee, debtor_fee, platform_fee, pay_now_amount, can_pay_later, pay_later_max_fee, total_loans, loan_fee, has_loans } = calc;
  const hasObligations = platform_fee > 0;

  return (
    <div className={`mt-6 border rounded-md overflow-hidden ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`} data-testid="cycle-calculator">
      <div className={`p-5 ${isDark ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
        <div className="flex items-center gap-2 mb-1">
          <Lightning size={22} weight="bold" className="text-yellow-400" />
          <h3 className={`font-outfit text-lg font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>TOR Calculator & Platform Fee</h3>
        </div>
        <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Pay the platform fee to enter the multilateral netting engine</p>
      </div>

      <div className={`p-5 space-y-5 ${isDark ? 'bg-zinc-950' : 'bg-white'}`}>
        {/* Fee breakdown */}
        <div className={`p-4 rounded-sm border ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`}>
          <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Platform Fee Breakdown</p>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className={`font-ibm text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>10% of what you owe ({symbol}{total_creditors.toLocaleString()})</span>
              <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{creditor_fee.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`font-ibm text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>5% of what's owed to you ({symbol}{total_debtors.toLocaleString()})</span>
              <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{debtor_fee.toLocaleString()}</span>
            </div>
            {has_loans && (
              <div className="flex justify-between items-center" data-testid="loan-fee-line">
                <span className={`font-ibm text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>20% of bank/NBFC loans ({symbol}{(total_loans || 0).toLocaleString()})</span>
                <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(loan_fee || 0).toLocaleString()}</span>
              </div>
            )}
            <div className={`border-t pt-2 flex justify-between items-center ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <span className={`font-ibm text-sm font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>Total Platform Fee</span>
              <span className={`font-outfit text-lg font-bold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>{symbol}{platform_fee.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className={`p-3 rounded-sm border ${isDark ? 'border-zinc-700 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="fee-info-text">
          <div className="flex gap-2">
            <Info size={16} className="text-yellow-400 shrink-0 mt-0.5" />
            <p className={`font-ibm text-xs leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              Diametor charges only this platform fee. Once paid (or your mandate is set), the engine automatically routes your debts and receivables through interlocking cycles to clear them — no units, no deposits.
            </p>
          </div>
        </div>

        {!hasObligations ? (
          <div className={`p-4 text-center font-ibm text-sm rounded-sm border ${isDark ? 'border-zinc-800 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`} data-testid="no-obligations">
            Add accepted debtors or creditors to calculate your platform fee.
          </div>
        ) : (
          <>
            {/* Pay Now */}
            <button onClick={() => openAgreement('now')} className="w-full p-4 rounded-sm bg-yellow-400 text-zinc-950 hover:bg-yellow-500 active:scale-[0.99] transition-all text-left" data-testid="pay-now-btn">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-outfit text-base font-bold flex items-center gap-2"><CurrencyCircleDollar size={18} weight="bold" /> Pay Now</p>
                  <p className="font-ibm text-xs opacity-80">Save 12% — pay upfront</p>
                </div>
                <div className="text-right">
                  <p className="font-outfit text-xl font-bold">{symbol}{pay_now_amount.toLocaleString()}</p>
                  <p className="font-ibm text-[10px] line-through opacity-70">{symbol}{platform_fee.toLocaleString()}</p>
                </div>
              </div>
            </button>
            <p className={`font-ibm text-[10px] -mt-2 px-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`} data-testid="refund-clause">
              If no clearing cycle is found and your obligations cannot be executed within <strong>90 days</strong>, this payment is fully refunded to the account you paid from.
            </p>

            {/* Pay Later */}
            <button onClick={() => can_pay_later && openAgreement('later')} disabled={!can_pay_later}
              className={`w-full p-4 rounded-sm border text-left transition-all ${can_pay_later ? (isDark ? 'border-zinc-700 hover:border-yellow-400/50 active:scale-[0.99]' : 'border-zinc-200 hover:border-yellow-400/50 active:scale-[0.99]') : `${isDark ? 'border-zinc-800 opacity-50' : 'border-zinc-200 opacity-50'} cursor-not-allowed`}`}
              data-testid="pay-later-btn">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`font-outfit text-base font-bold flex items-center gap-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}><ClockCountdown size={18} weight="bold" /> Pay Later (Mandate)</p>
                  <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{can_pay_later ? 'Set a mandate, charged on cycle execution' : `Only available for fees up to ${symbol}${pay_later_max_fee.toLocaleString()}`}</p>
                </div>
                <p className={`font-outfit text-xl font-bold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{platform_fee.toLocaleString()}</p>
              </div>
            </button>
            {has_loans && (
              <p className={`font-ibm text-[10px] -mt-2 px-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`} data-testid="loan-paynow-note">
                Your fee includes a bank/NBFC loan — loans can only be cleared via <strong>Pay Now</strong> (no mandate).
              </p>
            )}
          </>
        )}
      </div>

      <NovationAgreement
        open={agreementOpen}
        onClose={() => setAgreementOpen(false)}
        method={agreementMethod}
        feeInfo={calc}
        onAuthorize={handleAuthorize}
        submitting={submitting}
      />
    </div>
  );
}
